import type { GPUInstanceType } from "types/InstanceTypes";
import { hasVerbBaseImageContainer } from "components/Environment/Create/utils";
import type { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { buildDefaultVerbYamlConfig } from "components/Verb/utils";
import type {
  CreateLaunchableRequest,
  LaunchableBuildRequest,
  LaunchableCreateWorkspaceRequest,
  LaunchableFileRequest,
  LaunchableParameter,
  ModifyLaunchableRequest,
} from "server";
import agent from "server";
import { getPrimaryInstanceCloudCredId } from "utils/cloudCreds";
import type { AccessLevel } from "../shared/LaunchableAccessControl";
import type { LaunchableExposePorts } from "./types";
import { createFileRequest, transformPortListToMap } from "../utils";
import { normalizeLaunchableParameters } from "../shared/LaunchableParameters";

interface CreateWorkspaceOptions {
  imageId?: string;
  performDefaultBuildForCustomImage?: boolean;
  location?: string;
  subLocation?: string;
  parameters?: LaunchableParameter[];
}

export function makeCreateLaunchableRequest(
  name: string,
  description: string,
  viewAccess: "public" | "organization" | "published" = "public",
  storage: string,
  instance: GPUInstanceType,
  pythonVersion: string,
  cudaVersion: string,
  container: ContainerSelected,
  file: string,
  exposedPorts: LaunchableExposePorts,
  workspaceOptions: CreateWorkspaceOptions = {},
): CreateLaunchableRequest {
  const {
    imageId,
    performDefaultBuildForCustomImage,
    location,
    subLocation,
    parameters,
  } = workspaceOptions;

  const createWorkspaceRequest: LaunchableCreateWorkspaceRequest = {
    instanceType: instance.type,
    cloudCredId: getPrimaryInstanceCloudCredId(instance),
    storage: instance.elastic_root_volume ? storage : "",
    firewallRules: exposedPorts.firewallRules.map((rule) => ({
      port: rule.port.toString(),
      allowedIPs: rule.allowedIPs,
    })),
    ...(imageId
      ? {
          imageId,
          performDefaultBuildForCustomImage:
            !!performDefaultBuildForCustomImage,
        }
      : {}),
    ...(location ? { location } : {}),
    ...(subLocation ? { subLocation } : {}),
  };

  const buildRequest: LaunchableBuildRequest = {
    ports: exposedPorts.tunnels.map((tunnel) => ({
      name: tunnel.name,
      port: tunnel.port.toString(),
      labels:
        tunnel.isCTA || tunnel.isEnvironmentManaged
          ? {
              ...(tunnel.isCTA
                ? {
                    "cta-enabled": "true",
                    "cta-value": tunnel.ctaLabel || tunnel.name,
                  }
                : {}),
              ...(tunnel.isEnvironmentManaged
                ? { "managed-by": "docker-compose" }
                : {}),
            }
          : undefined,
    })),
    ...(parameters
      ? { parameters: normalizeLaunchableParameters(parameters) }
      : {}),
  };

  if (container.vmBuild) {
    // Build Request
    buildRequest.vmBuild = {
      forceJupyterInstall: !!container.vmBuild?.forceJupyterInstall,
      lifeCycleScriptAttr: container.vmBuild?.lifeCycleScriptAttr,
      k8s: container.vmBuild?.k8s
        ? {
            isDisabled: !!container.vmBuild.k8s.isDisabled,
            isDashboardEnabled: !!container.vmBuild.k8s.isDashboardEnabled,
          }
        : undefined,
    };
  }

  let jupyterPort = 8888;

  if (name.includes("blueprint") || name.includes("Blueprint")) {
    jupyterPort = 8887;
  }

  if (!!container.verbBuild) {
    const customYaml = buildDefaultVerbYamlConfig(
      hasVerbBaseImageContainer(container)
        ? container.verbBuild?.containerUrl
        : "",
      transformPortListToMap(
        exposedPorts.tunnels.map((tunnel) => ({
          name: tunnel.name,
          port: tunnel.port.toString(),
        })),
      ),
      cudaVersion,
      pythonVersion,
      jupyterPort,
      true,
    );
    buildRequest.verbBuild = {
      verbYaml: customYaml,
    };
  }

  if (!!container.customContainer) {
    buildRequest.containerBuild = {
      containerUrl: container.customContainer.containerUrl,
      entryPoint: container.customContainer.entryPoint,
    };
  }

  if (!!container.dockerCompose) {
    buildRequest.dockerCompose = {
      fileUrl: container.dockerCompose.fileUrl,
      jupyterInstall: container.dockerCompose.jupyterInstall,
      yamlString: container.dockerCompose.fileUrl
        ? undefined
        : container.dockerCompose.yamlString,
    };
  }

  // File Request
  let launchableFile: LaunchableFileRequest | null = null;
  const fileRequest = createFileRequest(file, container);
  if (fileRequest) {
    launchableFile = fileRequest[0];
  }

  return {
    name,
    description,
    viewAccess,
    createWorkspaceRequest: createWorkspaceRequest,
    buildRequest: buildRequest,
    file: launchableFile,
  };
}

export async function createLaunchable(
  orgId: string,
  createLaunchableRequest: CreateLaunchableRequest,
) {
  const res = await agent.Launchables.createEnvironmentLaunchable(
    orgId,
    createLaunchableRequest,
  );
  if (res.success && res.data) {
    return res.data;
  } else {
    return null;
  }
}

interface SaveLaunchableEditsArgs {
  orgId: string;
  request: ModifyLaunchableRequest;
  viewAccess: AccessLevel;
  originalViewAccess: AccessLevel;
  api?: Pick<
    typeof agent.Launchables,
    "editEnvironmentLaunchable" | "editLaunchableAccess"
  >;
}

export async function saveLaunchableEdits({
  orgId,
  request,
  viewAccess,
  originalViewAccess,
  api = agent.Launchables,
}: SaveLaunchableEditsArgs) {
  const accessChanged = viewAccess !== originalViewAccess;
  if (accessChanged) {
    const accessResponse = await api.editLaunchableAccess(orgId, {
      id: request.id,
      viewAccess,
    });
    if (!accessResponse.success) return accessResponse;
  }

  const editResponse = await api.editEnvironmentLaunchable(orgId, request);
  if (!editResponse.success && accessChanged) {
    try {
      await api.editLaunchableAccess(orgId, {
        id: request.id,
        viewAccess: originalViewAccess,
      });
    } catch {
      // Preserve the original edit failure; a subsequent reload reflects the server state.
    }
  }
  return editResponse;
}
